import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tokenStorage } from '@shared/services/storage/tokenStorage';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  mobile?: string;
  verified?: boolean;
  permissions?: Record<string, boolean>;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  tokenExpiry: number | null;
}

const initialState: AuthState = {
  accessToken: tokenStorage.getAccessToken(),
  refreshToken: tokenStorage.getRefreshToken(),
  user: loadUserFromStorage(),
  isAuthenticated: tokenStorage.hasTokens(),
  tokenExpiry: tokenStorage.getTokenExpiry(),
};

/**
 * Load user data from token storage
 * Safely handles cases where user data is missing or incomplete.
 * This prevents \"null is not an object (evaluating 'userData.userId.toString')\"
 * errors on initial app load when sessionStorage is empty or partially populated.
 */
function loadUserFromStorage(): User | null {
  const userData = tokenStorage.getUserData();

  // No stored user data or no role → treat as logged out
  if (!userData || !userData.role) {
    return null;
  }

  return {
    id: userData.userId != null && !Number.isNaN(userData.userId)
      ? String(userData.userId)
      : '',
    email: '', // Not stored in token — populated from API if needed
    name: userData.name || '',
    role: userData.role,
    mobile: userData.mobile,
    permissions: userData.permissions,
  };
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set authentication credentials after login
     */
    setCredentials: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        accessExpiryMillis: number;
        user?: User;
      }>
    ) => {
      const { accessToken, refreshToken, accessExpiryMillis, user } = action.payload;
      
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      // Update state
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.tokenExpiry = Date.now() + accessExpiryMillis;
      
      // Store in localStorage
      tokenStorage.setTokens(accessToken, refreshToken, accessExpiryMillis);

      // If user object not provided, load from storage (which was just updated by setTokens)
      if (!user) {
        state.user = loadUserFromStorage();
      } else {
        state.user = user;
      }
    },

    /**
     * Update only the access token (used after refresh)
     */
    updateAccessToken: (
      state,
      action: PayloadAction<{ accessToken: string; accessExpiryMillis: number }>
    ) => {
      const { accessToken, accessExpiryMillis } = action.payload;
      
      state.accessToken = accessToken;
      state.tokenExpiry = Date.now() + accessExpiryMillis;
      
      // Update in localStorage
      tokenStorage.setAccessToken(accessToken, accessExpiryMillis);

      // Refresh user data from new token
      state.user = loadUserFromStorage() || state.user;
    },

    /**
     * Set user information
     */
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },

    /**
     * Clear authentication state (logout)
     */
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      state.tokenExpiry = null;
      
      // Clear from localStorage
      tokenStorage.clearTokens();
    },

    /**
     * Check and update authentication status
     */
    checkAuthStatus: (state) => {
      const hasTokens = tokenStorage.hasTokens();

      if (!hasTokens) {
        // No refresh token — user must log in
        state.accessToken = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        state.tokenExpiry = null;
        tokenStorage.clearTokens();
      } else {
        // Refresh token exists — treat as authenticated.
        // The access token may be absent (normal after page refresh); it will
        // be obtained on the next API call via the refresh flow.
        state.accessToken = tokenStorage.getAccessToken();
        state.refreshToken = tokenStorage.getRefreshToken();
        state.tokenExpiry = tokenStorage.getTokenExpiry();
        state.isAuthenticated = true;
      }
    },
  },
});

export const { 
  setCredentials, 
  updateAccessToken,
  setUser,
  logout, 
  checkAuthStatus 
} = authSlice.actions;

export default authSlice.reducer;
